<script setup lang="ts">
import { BuilderContent } from '@builder.io/sdk-vue';
import { useElementVisibility } from '@vueuse/core';
import { useHead } from '@vueuse/head';
import { ComponentPublicInstance, computed, onMounted, ref, unref, watchEffect } from 'vue';
import { onBeforeRouteLeave, RouteLocationNormalized, useRoute } from 'vue-router';

import CmsContent from '@/components/cms/CmsContent.vue';
import StaticHomePage from '@/components/homepage/StaticHomePage.vue';
import Theme404 from '@/components/homepage/Theme404.vue';
import { isEditingSSR, useCms } from '@/composables/useCms';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useLayout } from '@/stores/layout';
import { trackFlagExperiment } from '@/utils/analytics/experimentViewedEvent';

interface CmsPage extends BuilderContent {
  data?: BuilderContent['data'] & {
    description?: string;
    excludeFooter: boolean;
    excludeHeader: boolean;
    keywords?: string[];
    previewImage?: string;
    searchable: boolean;
    title?: string;
    url?: string;
  };
}

const route = useRoute();
const layoutStore = useLayout();

const pageContent = ref<ComponentPublicInstance>();
const contentVisible = useElementVisibility(pageContent);
const { content } = useCms<CmsPage>('page', {
  contentVisible: () => contentVisible.value,
  prefetch: 'setup',
});

useHead(
  computed(() => {
    if (!content.value.result) return {};
    const contentData = unref(content.value.result.data);
    return {
      link: [{ rel: 'canonical', href: `https://nuts.com${contentData?.url ?? ''}` }],
      meta: [
        { name: 'description', content: contentData?.description },
        { name: 'keywords', content: contentData?.keywords?.join(',') },
        { name: 'robots', content: contentData?.searchable ? undefined : 'noindex,nofollow' },
        { property: 'og:description', content: contentData?.description, key: 'og:description' },
        { property: 'og:image', content: contentData?.previewImage, key: 'og:image' },
        { property: 'og:title', content: contentData?.title, key: 'og:title' },
        { property: 'og:url', content: `https://nuts.com${route.path}`, key: 'og:url' },
      ].filter((m) => !!m.content),
      title: contentData?.title?.concat(
        contentData.title.endsWith('Nuts.com') ? '' : ' - Nuts.com',
      ),
    };
  }),
);

onBeforeRouteLeave((to) => {
  if (!to.meta.resolveByWebstore && to.matched[0]?.components?.default.name !== 'CMS') {
    layoutStore.displayFooter = true;
    layoutStore.displayHeader = true;
  }
});

onMounted(() => {
  watchEffect(() => {
    if (content.value.result?.data?.excludeFooter) layoutStore.displayFooter = false;
    if (content.value.result?.data?.excludeHeader) layoutStore.displayHeader = false;
  });
});

defineOptions({
  beforeRouteEnter(to, _from, next) {
    const { flags, getFlag } = useFeatureFlags();
    if (to.path === '/custom/snackbox' && flags.enableCSBTest) {
      getFlag('enableSubscriptionOnCSB')();
      trackFlagExperiment(useFeatureFlags().flagsMetadata.enableSubscriptionOnCSB);
    }
    next();
  },
  async criticalData(_: any, to: RouteLocationNormalized) {
    if (isEditingSSR(to.query)) return; // do not SSR for visual editor; currently crashes during hydration
    const page = useCms<CmsPage>('page', { prefetch: 'criticalData' });
    if (page.content.value.loading) return; // currently mounting
    await page.loadCmsContent(to.path, to.query);
    const criticalLayoutStore = useLayout();
    criticalLayoutStore.displayFooter = !page.content.value.result?.data?.excludeFooter;
    criticalLayoutStore.displayHeader = !page.content.value.result?.data?.excludeHeader;
  },
});
</script>

<template>
  <div role="main">
    <CmsContent :content model="page" v-slot="slotProps" ref="pageContent">
      <StaticHomePage v-if="route.path === '/'" :fallbackReason="slotProps?.error" unexpected />
      <Theme404 v-else />
    </CmsContent>
  </div>
</template>
